'use client';

import { config } from './config';
import { getVisitorId, getSessionId, getAttributionData, buildCTAUrl } from './attribution';
import { getCachedViewerGeo } from './geo';

// ============================================================
// Types
// ============================================================

export interface PageContext {
    page_type: string;
    page_name: string;
    page_slug: string;
    page_category?: string;
    page_url?: string;
}

export interface CtaContext {
    cta_name: string;
    cta_label?: string;
    cta_position?: string;
    cta_variant?: string;
}

export interface CtaSourceContext extends PageContext {
    cta_name: string;
    cta_label?: string;
    cta_position?: string;
    cta_variant?: string;
    cta_clicked_at: string;
    visitor_id?: string;
    session_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    track_code?: string;
    long_code?: string;
    ref_type?: string;
    ref_event_id?: string | number;
    ref_host_id?: string | number;
}

// ============================================================
// Constants & Helpers
// ============================================================

const CTA_SOURCE_KEY = 'vv_cta_source_context';
const LANDING_PAGE_KEY = 'vv_landing_page_context';

function generateUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// ============================================================
// Landing page context — captured once per session
// ============================================================

export function captureLandingPage(pageContext: PageContext): void {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(LANDING_PAGE_KEY)) return; // already captured
    sessionStorage.setItem(LANDING_PAGE_KEY, JSON.stringify(pageContext));
}

export function getLandingPageContext(): PageContext | null {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(LANDING_PAGE_KEY);
    return raw ? JSON.parse(raw) : null;
}

// ============================================================
// CTA source context — persisted on each CTA click
// ============================================================

export function getStoredCtaSource(): CtaSourceContext | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(CTA_SOURCE_KEY);
    return raw ? JSON.parse(raw) : null;
}

export function clearStoredCtaSource(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(CTA_SOURCE_KEY);
}

// ============================================================
// Track CTA click
// ============================================================

export function trackCtaClick(
    pageContext: PageContext,
    ctaContext: CtaContext,
): void {
    if (typeof window === 'undefined') return;

    const now = new Date().toISOString();
    const attribution = getAttributionData();
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    // Build full CTA source context
    const ctaSource: CtaSourceContext = {
        ...pageContext,
        page_url: pageContext.page_url || window.location.pathname,
        cta_name: ctaContext.cta_name,
        cta_label: ctaContext.cta_label,
        cta_position: ctaContext.cta_position,
        cta_variant: ctaContext.cta_variant,
        cta_clicked_at: now,
        visitor_id: visitorId,
        session_id: sessionId,
        utm_source: attribution?.utm_source,
        utm_medium: attribution?.utm_medium,
        utm_campaign: attribution?.utm_campaign,
        utm_content: attribution?.utm_content,
        utm_term: attribution?.utm_term,
        track_code: attribution?.track_code,
        long_code: attribution?.long_code,
        ref_type: attribution?.ref_type,
        ref_event_id: attribution?.ref_event_id,
        ref_host_id: attribution?.ref_host_id,
    };

    // Persist to localStorage (survives navigation to signup/create-event)
    localStorage.setItem(CTA_SOURCE_KEY, JSON.stringify(ctaSource));

    // Fire tracking event to backend
    const eventPayload = {
        schema_version: '1.1',
        environment: process.env.NODE_ENV || 'production',
        idempotency_key: `evt-${generateUUID()}`,
        event_name: 'cta_clicked',
        event_category: 'acquisition',
        session_id: sessionId,
        visitor_id: visitorId,
        page_url: ctaSource.page_url,
        page_path: window.location.pathname,
        page_type: pageContext.page_type,
        page_name: pageContext.page_name,
        page_slug: pageContext.page_slug,
        page_category: pageContext.page_category,
        cta_name: ctaContext.cta_name,
        cta_label: ctaContext.cta_label,
        cta_position: ctaContext.cta_position,
        cta_variant: ctaContext.cta_variant,
        utm_source: ctaSource.utm_source,
        utm_medium: ctaSource.utm_medium,
        utm_campaign: ctaSource.utm_campaign,
        utm_content: ctaSource.utm_content,
        utm_term: ctaSource.utm_term,
        track_code: ctaSource.track_code,
        long_code: ctaSource.long_code,
        ref_type: ctaSource.ref_type,
        ref_event_id: ctaSource.ref_event_id,
        ref_host_id: ctaSource.ref_host_id,
    };

    const endpoint = `${config.API_BASE_URL}/tracking/event`;
    const body = JSON.stringify(eventPayload);

    if (navigator.sendBeacon) {
        navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    } else {
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch(() => { /* non-blocking */ });
    }

    // Also update session with CTA source context
    const sessionPayload = {
        schema_version: '1.1',
        environment: process.env.NODE_ENV || 'production',
        session_id: sessionId,
        visitor_id: visitorId,
        source_page_type: pageContext.page_type,
        source_page_name: pageContext.page_name,
        source_page_slug: pageContext.page_slug,
        source_page_category: pageContext.page_category,
        source_page_url: ctaSource.page_url,
        cta_name: ctaContext.cta_name,
        cta_label: ctaContext.cta_label,
        cta_position: ctaContext.cta_position,
        cta_variant: ctaContext.cta_variant,
        cta_clicked_at: now,
        // Geo resolved at page load, if any — this beacon fires during navigation, so don't wait on a fetch.
        ...getCachedViewerGeo(),
    };

    const sessionEndpoint = `${config.API_BASE_URL}/tracking/session`;
    const sessionBody = JSON.stringify(sessionPayload);

    if (navigator.sendBeacon) {
        navigator.sendBeacon(sessionEndpoint, new Blob([sessionBody], { type: 'application/json' }));
    } else {
        fetch(sessionEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: sessionBody,
            keepalive: true,
        }).catch(() => { /* non-blocking */ });
    }
}

// ============================================================
// Track page view
// ============================================================

export function trackPageView(pageContext: PageContext): void {
    if (typeof window === 'undefined') return;

    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const attribution = getAttributionData();

    const eventPayload = {
        schema_version: '1.1',
        environment: process.env.NODE_ENV || 'production',
        idempotency_key: `evt-${generateUUID()}`,
        event_name: 'page_view',
        event_category: 'acquisition',
        session_id: sessionId,
        visitor_id: visitorId,
        page_url: pageContext.page_url || window.location.pathname,
        page_path: window.location.pathname,
        page_type: pageContext.page_type,
        page_name: pageContext.page_name,
        page_slug: pageContext.page_slug,
        page_category: pageContext.page_category,
        utm_source: attribution?.utm_source,
        utm_medium: attribution?.utm_medium,
        utm_campaign: attribution?.utm_campaign,
        track_code: attribution?.track_code,
        ref_type: attribution?.ref_type,
    };

    const endpoint = `${config.API_BASE_URL}/tracking/event`;
    const body = JSON.stringify(eventPayload);

    if (navigator.sendBeacon) {
        navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    } else {
        fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch(() => { /* non-blocking */ });
    }
}

// ============================================================
// Build CTA URL with page/CTA context forwarded as query params
// ============================================================

export function buildTrackedCTAUrl(
    baseUrl: string,
    pageContext: PageContext,
    ctaContext: CtaContext,
): string {
    if (typeof window === 'undefined') return baseUrl;

    // Start with attribution-enriched URL
    const url = new URL(buildCTAUrl(baseUrl));

    // Add page context
    url.searchParams.set('vv_pt', pageContext.page_type);
    url.searchParams.set('vv_ps', pageContext.page_slug);
    if (pageContext.page_category) url.searchParams.set('vv_pc', pageContext.page_category);

    // Add CTA context
    url.searchParams.set('vv_cn', ctaContext.cta_name);
    if (ctaContext.cta_position) url.searchParams.set('vv_cp', ctaContext.cta_position);
    if (ctaContext.cta_variant) url.searchParams.set('vv_cv', ctaContext.cta_variant);

    return url.toString();
}
